import {
    Component,
    DestroyRef,
    ElementRef,
    ViewChild,
    effect,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NgFor, NgIf } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
    CmdkModule,
} from '../../shared/components/cmdk/cmdk.module';
import { debounceTime, distinctUntilChanged, finalize, switchMap, take } from 'rxjs/operators';
import { GlobalCommandPaletteService } from '../../core/services/global-command-palette/global-command-palette.service';
import { GlobalCrmSearchService } from '../../core/services/global-command-palette/global-crm-search.service';
import { BillingEntitlementsStateService } from '../../modules/subscription/services/billing-entitlements-state.service';
import {
    COMMAND_PALETTE_ACTION_ITEMS,
    COMMAND_PALETTE_NAV_ITEMS,
} from '../../core/services/global-command-palette/command-palette.registry';
import type {
    CommandPaletteItem,
    CommandPaletteRecordGroup,
} from '../../core/services/global-command-palette/command-palette.types';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideActivity, lucideBriefcaseBusiness, lucideBuilding2, lucideCreditCard, lucideHelpCircle, lucideHome, lucideMail, lucideMegaphone, lucideSettings, lucideTarget, lucideUsers } from '@ng-icons/lucide';

@Component({
    selector: 'app-global-command-palette',
    standalone: true,
    imports: [
        NgIf,
        NgFor,
        CmdkModule,
        NgIconComponent,
    ],
    providers: [
        provideIcons({
            lucideHome,
            lucideUsers,
            lucideBuilding2,
            lucideBriefcaseBusiness,
            lucideActivity,
            lucideMail,
            lucideSettings,
            lucideCreditCard,
            lucideHelpCircle,
            lucideMegaphone,
            lucideTarget,
        }),
    ],
    templateUrl: './global-command-palette.component.html',
})
export class GlobalCommandPaletteComponent {
    readonly paletteService = inject(GlobalCommandPaletteService);
    private readonly searchService = inject(GlobalCrmSearchService);
    private readonly entitlementsState = inject(BillingEntitlementsStateService);
    private readonly router = inject(Router);
    private readonly store = inject(Store);
    private readonly destroyRef = inject(DestroyRef);

    @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

    readonly query = signal('');
    readonly loading = signal(false);
    readonly recordGroups = signal<CommandPaletteRecordGroup[]>([]);
    readonly navItems = COMMAND_PALETTE_NAV_ITEMS;
    readonly actionItems = COMMAND_PALETTE_ACTION_ITEMS;

    get visibleNavItems(): CommandPaletteItem[] {
        return this.filterUnlockedItems(this.navItems);
    }

    get visibleActionItems(): CommandPaletteItem[] {
        return this.filterUnlockedItems(this.actionItems);
    }

    private filterUnlockedItems(items: CommandPaletteItem[]): CommandPaletteItem[] {
        if (this.entitlementsState.isLoading() || !this.entitlementsState.entitlements()) {
            return items;
        }
        return items.filter(
            (item) => !item.featureKey || this.entitlementsState.isFeatureIncluded(item.featureKey),
        );
    }

    readonly paletteFilter = (value: string, search: string): boolean => {
        if (value.startsWith('record:')) {
            return true;
        }

        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) {
            return true;
        }

        return value.toLowerCase().includes(normalizedSearch);
    };

    constructor() {
        toObservable(this.query)
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                switchMap((searchQuery) => {
                    this.loading.set(true);
                    return this.searchService.search(searchQuery).pipe(
                        finalize(() => this.loading.set(false)),
                    );
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((groups) => this.recordGroups.set(groups));

        effect(() => {
            if (!this.paletteService.isOpen()) {
                this.query.set('');
                this.recordGroups.set([]);
                this.loading.set(false);
                return;
            }

            queueMicrotask(() => this.searchInput?.nativeElement.focus());
        });
    }

    onBackdropClick(): void {
        this.paletteService.close();
    }

    onQueryInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.query.set(value);
    }

    onSelect(item: CommandPaletteItem): void {
        if (item.kind === 'action' && item.actionId === 'toggle-theme') {
            this.store
                .select((state) => state.index?.theme)
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe((theme) => {
                    const nextTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
                    this.store.dispatch({ type: 'toggleTheme', payload: nextTheme });
                    this.paletteService.close();
                });
            return;
        }

        if (item.route) {
            void this.router.navigateByUrl(item.route);
        }

        this.paletteService.close();
    }

    get showRecordResults(): boolean {
        return this.query().trim().length >= 2;
    }

    get showShortQueryHint(): boolean {
        const trimmed = this.query().trim();
        return trimmed.length > 0 && trimmed.length < 2;
    }
}
